import { useDepartmentText } from 'model/DepartmentTerminology';
import { Checkbox, Divider, Group, Modal, ScrollArea, Stack, TextInput, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconChevronLeft, IconChevronRight, IconSearch } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import P2Regular from 'components/typography/P2Regular';
import { useEffect, useMemo, useState } from 'react';
import panic from 'errors/panic';
import PermissionTemplateInput from 'components/inputs/permission-template-input/PermissionTemplateInput';
import { DepartmentListResponse } from 'api/actions/department-list/department-list-response';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';
import { DepartmentBreadcrumbItem } from 'components/department/DepartmentBreadcrumb';
import { DepartmentSelect } from 'components/selects/DepartmentSelect';
import useDepartmentUiMode from 'model/useDepartmentUiMode';
import {
  ALL_ORGANIZATION_MODULES_DISABLED,
  combineOrganizationModuleFlags,
  OrganizationModuleFlags,
} from 'features/organization-modules/use-organization-modules';

type Step = 'departments' | 'permission';

interface PickUsersModalParams {
  title: React.ReactNode;
  permissionsTitle?: string;
  opened: boolean;
  hiddenDepartments: number[];
  pickDepartments?: boolean;
  pickPermissions?: boolean;
  targetDepartmentIds?: number[];
  initialStep?: Step;
  initialTemplateName: string;
  initialPermissions: number[];
  inheritedPermissionsByDepartment?: Record<number, number[]>;
  onClose: () => void;
  onPick: (value: {
    pickedDepartments: {
      [departmentId: number]: {
        checked: boolean;
        templateName: string;
        departmentName: string;
        breadcrumbs: DepartmentBreadcrumbItem[];
        moduleFlags: OrganizationModuleFlags;
      };
    };
    permissions: number[];
    templateName: string;
  }) => void;
}

type FormValues = {
  pickedDepartments: {
    [departmentId: number]: {
      checked: boolean;
      templateName: string;
      departmentName: string;
      breadcrumbs: DepartmentBreadcrumbItem[];
      moduleFlags: OrganizationModuleFlags;
    };
  };
  permissions: { templateName: string; permissions: number[] };
};

type Department = DepartmentListResponse[number] & { moduleFlags: OrganizationModuleFlags };

/** Orders departments as a canonical tree: parent before descendants and siblings by name. */
function compareDepartmentTree(left: Department, right: Department) {
  const organizationOrder = left.organization.organizationId - right.organization.organizationId;

  if (organizationOrder !== 0) return organizationOrder;

  const sharedDepth = Math.min(left.breadcrumbs.length, right.breadcrumbs.length);

  for (let index = 0; index < sharedDepth; index += 1) {
    const leftItem = left.breadcrumbs[index];
    const rightItem = right.breadcrumbs[index];

    if (leftItem.departmentId === rightItem.departmentId) continue;

    const nameOrder = leftItem.departmentName.localeCompare(rightItem.departmentName, 'sk');
    return nameOrder || leftItem.departmentId - rightItem.departmentId;
  }

  return left.breadcrumbs.length - right.breadcrumbs.length;
}

/**
 * Modal for selecting permissions of a user in a department.
 */
export default function PickUsersModal({
  title,
  permissionsTitle,
  hiddenDepartments,
  opened,
  pickDepartments = true,
  pickPermissions = true,
  targetDepartmentIds = [],
  initialTemplateName,
  initialPermissions,
  inheritedPermissionsByDepartment = {},
  onClose,
  onPick,
}: PickUsersModalParams) {
  const departmentText = useDepartmentText();
  const { mode } = useDepartmentUiMode();
  const { isTablet } = useResponsiveDesign();
  const [step, setStep] = useState<'departments' | 'permission'>(pickDepartments ? 'departments' : 'permission');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentsLoaded, setDepartmentsLoaded] = useState(false);
  const [search, setSearch] = useState('');

  const { getAction } = useApi();
  const theme = useMantineTheme();

  const form = useForm<FormValues>({
    initialValues: {
      pickedDepartments: {},
      permissions: { permissions: initialPermissions, templateName: initialTemplateName },
    },
  });
  const flatDepartments = useMemo(
    () =>
      departments.filter(({ departmentName }) =>
        departmentName.toLocaleLowerCase('sk').includes(search.toLocaleLowerCase('sk'))
      ),
    [departments, search]
  );

  const moduleFlags = combineOrganizationModuleFlags(
    departments
      .filter((department) =>
        pickDepartments
          ? form.values.pickedDepartments[department.departmentId]?.checked
          : targetDepartmentIds.includes(department.departmentId)
      )
      .map((department) => department.moduleFlags)
  );
  const inheritedPermissions = Array.from(
    new Set(
      departments
        .filter((department) =>
          pickDepartments
            ? form.values.pickedDepartments[department.departmentId]?.checked
            : targetDepartmentIds.includes(department.departmentId)
        )
        .flatMap((department) => inheritedPermissionsByDepartment[department.departmentId] ?? [])
    )
  );

  /**
   * Submits the form.
   */
  function submit({ pickedDepartments, permissions }: FormValues) {
    onPick({ pickedDepartments, permissions: permissions.permissions, templateName: permissions.templateName });
    onClose();
  }

  useEffect(() => {
    const departmentGetListAction = getAction('DepartmentList');
    const organizationGetListAction = getAction('OrganizationList');
    // retrieve all people from the app
    // the backend will take care of filtering out the ones the current user doesn't have access to
    Promise.all([
      departmentGetListAction({
        query: { filters: { 'showDiscarded.eq': 0, 'permissionSlug.eq': 'manage-users' } },
      }),
      organizationGetListAction({
        query: { filters: { 'showDiscarded.eq': 0, 'permissionSlug.eq': 'manage-users' } },
      }),
    ])
      .then(([departments, organizations]) => {
        const organizationModuleFlags = organizations.reduce((acc, organization) => {
          acc[organization.organizationId] = {
            priceOffersEnabled: organization.priceOffersEnabled,
            hrEnabled: organization.hrEnabled,
            revisionsEnabled: organization.revisionsEnabled,
            formsEnabled: organization.formsEnabled,
            revisionTemplatesEnabled: organization.revisionTemplatesEnabled,
          };
          return acc;
        }, {} as Record<number, OrganizationModuleFlags>);
        const filtered = departments
          .filter(({ departmentId }) => !hiddenDepartments.includes(departmentId))
          .map((department) => ({
            ...department,
            moduleFlags:
              organizationModuleFlags[department.organization.organizationId] ?? ALL_ORGANIZATION_MODULES_DISABLED,
          }))
          .sort(compareDepartmentTree);

        const reduced = filtered.reduce((acc, curr) => {
          acc[curr.departmentId] = {
            checked: false,
            templateName: 'no-template',
            departmentName: curr.departmentName,
            breadcrumbs: curr.breadcrumbs,
            moduleFlags: curr.moduleFlags,
          };
          return acc;
        }, {} as FormValues['pickedDepartments']);

        form.setFieldValue('pickedDepartments', reduced);

        setDepartments(filtered);
        setDepartmentsLoaded(true);
      })
      .catch(panic);
  }, [getAction]);

  const multiStep = pickDepartments && pickPermissions;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        step === 'departments'
          ? title
          : multiStep && step === 'permission'
          ? departmentText('Nastaviť oprávnenia v položke úrovne', 'Nastaviť oprávnenia v stredisku')
          : permissionsTitle
      }
      centered
      size="xl"
    >
      <Divider color="gray.1" mb={24} />
      <form onSubmit={form.onSubmit(submit)}>
        {step === 'departments' ? (
          mode === 'legacy' ? (
            <Stack spacing={24}>
              <TextInput
                rightSection={<IconSearch stroke="1.5" color={theme.colors.gray[6]} />}
                value={search}
                placeholder="Hľadať strediská"
                size="md"
                onChange={(event) => setSearch(event.currentTarget.value)}
                name="objekto-search-departments"
              />
              <ScrollArea h={410}>
                <Stack spacing={24} pb={24}>
                  {flatDepartments.map((department) => (
                    <Checkbox
                      key={department.departmentId}
                      label={department.departmentName}
                      size="lg"
                      name={`objekto-pickedDepartments.${department.departmentId}.checked`}
                      {...form.getInputProps(`pickedDepartments.${department.departmentId}.checked`, {
                        type: 'checkbox',
                      })}
                    />
                  ))}
                  {departmentsLoaded && flatDepartments.length === 0 && (
                    <P2Regular>Neboli nájdené žiadne strediská.</P2Regular>
                  )}
                </Stack>
              </ScrollArea>
            </Stack>
          ) : (
            <DepartmentSelect
              multiple
              inline
              confirmSelection={false}
              selectSubtrees
              pruneCoveredIds
              groupByOrganization
              excludedIds={hiddenDepartments}
              coveredByIds={hiddenDepartments}
              preloadedDepartments={departments}
              loading={!departmentsLoaded}
              emptyText={departmentText('Neboli nájdené žiadne položky úrovne.', 'Neboli nájdené žiadne strediská.')}
              value={Object.entries(form.values.pickedDepartments)
                .filter(([, department]) => department.checked)
                .map(([id]) => id)}
              onChange={(ids) => {
                const selectedIds = new Set(ids);
                form.setFieldValue(
                  'pickedDepartments',
                  Object.fromEntries(
                    Object.entries(form.values.pickedDepartments).map(([id, department]) => [
                      id,
                      { ...department, checked: selectedIds.has(id) },
                    ])
                  )
                );
              }}
            />
          )
        ) : (
          departmentsLoaded && (
            <PermissionTemplateInput
              {...form.getInputProps('permissions')}
              permissionType="department"
              moduleFlags={moduleFlags}
              inheritedPermissions={inheritedPermissions}
            />
          )
        )}

        {/* TODO find a proper place to display this error */}
        <P2Regular color="red.9">{form.errors.pickedDepartments}</P2Regular>
        <P2Regular color="red.9">{form.errors.permissions}</P2Regular>

        <Group spacing={isTablet ? 20 : 40} position="apart" mt={24}>
          <Button
            variant="subtle-gray"
            type="button"
            leftIcon={
              multiStep && step === 'permission' ? (
                <IconChevronLeft stroke="1.5" color={theme.colors.gray[7]} />
              ) : undefined
            }
            onClick={() => {
              if (multiStep && step === 'permission') {
                setStep('departments');
              } else {
                onClose();
              }
            }}
          >
            {multiStep && step === 'permission' ? 'Späť' : 'Zrušiť'}
          </Button>
          {step === 'departments' && (
            <Button
              variant="primary"
              size="md"
              type="button"
              rightIcon={<IconChevronRight stroke="1.5" color={theme.colors.gray[0]} />}
              onClick={() => setStep('permission')}
              disabled={Object.values(form.values.pickedDepartments).every((value) => !value.checked)}
            >
              Pokračovať
            </Button>
          )}
          {step === 'permission' && (
            <Button
              variant="primary"
              size="md"
              type="submit"
              disabled={!departmentsLoaded || form.values.permissions.permissions.length === 0}
            >
              Uložiť {multiStep && departmentText('a pridať položky úrovne', 'a pridať strediská')}
            </Button>
          )}
        </Group>
      </form>
    </Modal>
  );
}
